import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Webhook as WebhookIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Science as TestIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const Webhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [form, setForm] = useState({ 
    name: '', 
    url: '', 
    method: 'POST', 
    company_id: '', 
    events: 'chat.created,chat.replied', 
    headers: '{"Content-Type":"application/json"}', 
    payload_template: '{"event":"{{event}}","data":{{data}}}',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const eventOptions = [
    'chat.created',
    'chat.replied',
    'chat.ended',
    'user.registered',
    'user.login',
    'bot.activated',
    'bot.deactivated',
    'message.sent',
    'message.received'
  ];

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const res = await axios.get('https://backend.mtsdemo.de/api/webhooks/');
      setWebhooks(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Webhooks:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Webhooks', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/`);
      setCompanies(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Firmen:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Firmen', severity: 'error' });
    }
  };

  useEffect(() => {
    loadWebhooks();
    loadCompanies();
  }, []);

  const filteredWebhooks = webhooks.filter(webhook =>
    webhook.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    webhook.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    webhook.company?.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateWebhook = async () => {
    if (!form.name || !form.url) {
      setSnackbar({ open: true, message: 'Bitte füllen Sie alle Pflichtfelder aus', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        company_id: form.company_id ? Number(form.company_id) : null,
        events: (form.events || '').split(',').map(e => e.trim()).filter(Boolean),
        headers: JSON.parse(form.headers || '{}'),
      };
      await axios.post('https://backend.mtsdemo.de/api/webhooks/', payload);
      setOpen(false);
      resetForm();
      await loadWebhooks();
      setSnackbar({ open: true, message: 'Webhook erfolgreich erstellt', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Erstellen des Webhooks:', error);
      setSnackbar({ open: true, message: 'Fehler beim Erstellen des Webhooks', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWebhook = async () => {
    if (!editingWebhook) return;

    try {
      setSaving(true);
      const payload = {
        ...form,
        company_id: form.company_id ? Number(form.company_id) : null,
        events: (form.events || '').split(',').map(e => e.trim()).filter(Boolean),
        headers: JSON.parse(form.headers || '{}'),
      };
      await axios.put(`https://backend.mtsdemo.de/api/webhooks/${editingWebhook.id}`, payload);
      setOpen(false);
      setEditingWebhook(null);
      resetForm();
      await loadWebhooks();
      setSnackbar({ open: true, message: 'Webhook erfolgreich aktualisiert', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Webhooks:', error);
      setSnackbar({ open: true, message: 'Fehler beim Aktualisieren des Webhooks', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (window.confirm('Sind Sie sicher, dass Sie diesen Webhook löschen möchten?')) {
      try {
        await axios.delete(`https://backend.mtsdemo.de/api/webhooks/${id}`);
        await loadWebhooks();
        setSnackbar({ open: true, message: 'Webhook erfolgreich gelöscht', severity: 'success' });
      } catch (error) {
        console.error('Fehler beim Löschen des Webhooks:', error);
        setSnackbar({ open: true, message: 'Fehler beim Löschen des Webhooks', severity: 'error' });
      }
    }
  };

  const handleEditWebhook = (webhook) => {
    setEditingWebhook(webhook);
    setForm({
      name: webhook.name || '',
      url: webhook.url || '',
      method: webhook.method || 'POST',
      company_id: webhook.company_id || '',
      events: (webhook.events || []).join(', '),
      headers: JSON.stringify(webhook.headers || {}, null, 2),
      payload_template: webhook.payload_template || '{"event":"{{event}}","data":{{data}}}',
      is_active: webhook.is_active !== false
    });
    setOpen(true);
  };

  const handleTestWebhook = async (webhook) => {
    try {
      // Hier würde normalerweise ein Test-Request gesendet werden
      setSnackbar({ open: true, message: 'Test-Request gesendet', severity: 'info' });
    } catch (error) {
      console.error('Fehler beim Testen des Webhooks:', error);
      setSnackbar({ open: true, message: 'Fehler beim Testen des Webhooks', severity: 'error' });
    }
  };

  const resetForm = () => {
    setForm({ 
      name: '', 
      url: '', 
      method: 'POST', 
      company_id: '', 
      events: 'chat.created,chat.replied', 
      headers: '{"Content-Type":"application/json"}', 
      payload_template: '{"event":"{{event}}","data":{{data}}}',
      is_active: true
    });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingWebhook(null);
    resetForm();
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Aktiv' : 'Inaktiv';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WebhookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Webhooks</Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          Neuer Webhook
        </Button>
      </Box>

      {/* Suchfeld */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Webhooks durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {filteredWebhooks.length} von {webhooks.length} Webhooks angezeigt
          </Typography>
        </CardContent>
      </Card>

      {/* Webhooks Tabelle */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>URL</strong></TableCell>
              <TableCell><strong>Firma</strong></TableCell>
              <TableCell><strong>Methode</strong></TableCell>
              <TableCell><strong>Events</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Aktionen</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWebhooks.map((webhook) => (
              <TableRow key={webhook.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WebhookIcon color="primary" />
                    <Typography variant="body1" fontWeight="medium">
                      {webhook.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {webhook.url}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="primary" />
                    <Typography variant="body2">
                      {webhook.company?.display_name || 'Global'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={webhook.method} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(webhook.events || []).slice(0, 2).map((event, index) => (
                      <Chip 
                        key={index}
                        label={event} 
                        size="small" 
                        variant="outlined"
                      />
                    ))}
                    {(webhook.events || []).length > 2 && (
                      <Chip 
                        label={`+${(webhook.events || []).length - 2}`} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(webhook.is_active)}
                    color={getStatusColor(webhook.is_active)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="Bearbeiten">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditWebhook(webhook)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Testen">
                      <IconButton 
                        size="small" 
                        onClick={() => handleTestWebhook(webhook)}
                        color="info"
                      >
                        <TestIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredWebhooks.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <WebhookIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'Keine Webhooks gefunden' : 'Noch keine Webhooks vorhanden'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff' : 'Erstellen Sie Ihren ersten Webhook'}
          </Typography>
        </Box>
      )}

      {/* Dialog für Webhook erstellen/bearbeiten */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {editingWebhook ? 'Webhook bearbeiten' : 'Neuen Webhook erstellen'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Grundlegende Einstellungen */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon color="primary" />
                  <Typography>Grundlegende Einstellungen</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Name" 
                      value={form.name} 
                      onChange={(e) => setForm({ ...form, name: e.target.value })} 
                      fullWidth 
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Methode</InputLabel>
                      <Select
                        value={form.method}
                        onChange={(e) => setForm({ ...form, method: e.target.value })}
                        label="Methode"
                      >
                        {['POST', 'PUT', 'PATCH'].map(method => (
                          <MenuItem key={method} value={method}>{method}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      label="URL" 
                      value={form.url} 
                      onChange={(e) => setForm({ ...form, url: e.target.value })} 
                      fullWidth 
                      required
                      placeholder="https://example.com/webhook"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Firma (optional)</InputLabel>
                      <Select
                        value={form.company_id}
                        onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                        label="Firma (optional)"
                      >
                        <MenuItem value="">Global (alle Firmen)</MenuItem>
                        {companies.map((company) => (
                          <MenuItem key={company.id} value={company.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon />
                              <Typography>{company.display_name}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.is_active}
                          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        />
                      }
                      label="Webhook ist aktiv"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Events */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Events</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField 
                      label="Events (kommagetrennt)" 
                      value={form.events} 
                      onChange={(e) => setForm({ ...form, events: e.target.value })} 
                      fullWidth 
                      multiline
                      rows={3}
                      helperText="Verfügbare Events: chat.created, chat.replied, chat.ended, user.registered, user.login, bot.activated, bot.deactivated, message.sent, message.received"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Verfügbare Events:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {eventOptions.map((event) => (
                        <Chip 
                          key={event}
                          label={event} 
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            const currentEvents = form.events.split(',').map(e => e.trim()).filter(Boolean);
                            if (!currentEvents.includes(event)) {
                              setForm({ ...form, events: [...currentEvents, event].join(', ') });
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Headers */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Headers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField 
                  label="Headers (JSON)" 
                  value={form.headers} 
                  onChange={(e) => setForm({ ...form, headers: e.target.value })} 
                  fullWidth 
                  multiline
                  rows={4}
                  helperText="JSON-Format für HTTP-Headers"
                />
              </AccordionDetails>
            </Accordion>

            {/* Payload Template */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Payload Template</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField 
                  label="Payload Template" 
                  value={form.payload_template} 
                  onChange={(e) => setForm({ ...form, payload_template: e.target.value })} 
                  fullWidth 
                  multiline
                  rows={4}
                  helperText="Template für die Webhook-Payload. Verfügbare Variablen: {{event}}, {{data}}, {{timestamp}}"
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button 
            onClick={editingWebhook ? handleUpdateWebhook : handleCreateWebhook} 
            disabled={saving || !form.name || !form.url} 
            variant="contained"
          >
            {saving ? 'Speichern...' : (editingWebhook ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Webhooks;